import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Capture a DOM element to a clean, bordered A4 PDF and download it.
// Multi-page safe: the content is sliced across pages and a border box is
// drawn on each page. No browser print dialog, no preview scrollbar.
export async function downloadElementAsPdf(el, filename = 'document') {
  if (!el) return;
  // Render an OFF-SCREEN clone at a fixed desktop A4 width. On a phone the live
  // element is narrow (and laid out single-column), so html2canvas would capture
  // a tall canvas that spills onto 2–3 pages. Cloning into a fixed-width wrapper
  // guarantees the invoice is captured as a proper one-page A4 on any device.
  const A4_W = 794; // ≈ 210mm at 96dpi
  const wrapper = document.createElement('div');
  wrapper.style.cssText = `position:fixed; left:-10000px; top:0; width:${A4_W}px; background:#ffffff; z-index:-1;`;
  const clone = el.cloneNode(true);
  clone.style.width = A4_W + 'px';
  clone.style.minWidth = A4_W + 'px';
  clone.style.maxWidth = A4_W + 'px';
  clone.style.margin = '0';
  // Disable mobile font-boosting so text isn't auto-inflated (which would wrap
  // more and push the invoice onto extra pages on phones).
  clone.style.webkitTextSizeAdjust = '100%';
  clone.style.textSizeAdjust = '100%';
  wrapper.appendChild(clone);
  document.body.appendChild(wrapper);

  let canvas;
  try {
    canvas = await html2canvas(clone, { scale: 2, backgroundColor: '#ffffff', useCORS: true, logging: false });
  } finally {
    document.body.removeChild(wrapper);
  }
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageW = 210, pageH = 297, margin = 10;
  const contentW = pageW - margin * 2;   // 190mm
  const contentH = pageH - margin * 2;   // 277mm
  // Fit the whole invoice onto ONE page, preserving aspect ratio. Fits to the
  // page width normally; if the capture is unusually tall (e.g. a phone laid it
  // out narrow), it scales down to fit the page height instead — still one page.
  let imgW = contentW;
  let imgH = imgW * (canvas.height / canvas.width);
  if (imgH > contentH) {
    imgH = contentH;
    imgW = imgH * (canvas.width / canvas.height);
  }
  const x = margin + (contentW - imgW) / 2;
  pdf.addImage(canvas.toDataURL('image/png'), 'PNG', x, margin, imgW, imgH);
  // Clean border around the invoice.
  pdf.setDrawColor(31, 41, 55);
  pdf.setLineWidth(0.3);
  pdf.rect(x, margin, imgW, imgH);
  pdf.save(`${filename}`.replace(/\s+/g, '_') + '.pdf');
}

// Build a clean, multi-page landscape A4 PDF of a data table (used by the
// "Download PDF" button on every list/report). Columns follow the DataTable
// shape ({ key, label, align, exportValue }); cell text uses exportValue when
// provided, otherwise the raw row value.
export async function downloadTableAsPdf(title, columns, rows) {
  const W = 1050; // landscape A4 content width in px
  const cellText = (col, row) => {
    if (col.exportValue) { try { return String(col.exportValue(row) ?? ''); } catch { /* ignore */ } }
    const v = row[col.key];
    return v == null ? '' : String(v);
  };

  const wrapper = document.createElement('div');
  wrapper.style.cssText = `position:fixed; left:-10000px; top:0; width:${W}px; background:#ffffff; z-index:-1; padding:8px; font-family:Inter,Arial,sans-serif; -webkit-text-size-adjust:100%;`;
  const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const head = columns.map(c => `<th style="text-align:${c.align === 'right' ? 'right' : 'left'}; padding:6px 8px; border-bottom:2px solid #1f2937; font-size:12px; color:#374151; white-space:nowrap;">${esc(c.label || '')}</th>`).join('');
  const body = rows.map((r, i) => `<tr style="background:${i % 2 ? '#f9fafb' : '#ffffff'};">${columns.map(c => `<td style="text-align:${c.align === 'right' ? 'right' : 'left'}; padding:5px 8px; border-bottom:1px solid #eee; font-size:11px; color:#111827;">${esc(cellText(c, r))}</td>`).join('')}</tr>`).join('');
  wrapper.innerHTML = `
    <div style="font-size:18px; font-weight:700; color:#111827; margin-bottom:2px;">${esc(title || 'Report')}</div>
    <div style="font-size:11px; color:#6b7280; margin-bottom:10px;">${rows.length} record(s) · Generated ${new Date().toLocaleDateString()}</div>
    <table style="width:100%; border-collapse:collapse;"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
  document.body.appendChild(wrapper);

  let canvas;
  try {
    canvas = await html2canvas(wrapper, { scale: 2, backgroundColor: '#ffffff', useCORS: true, logging: false });
  } finally {
    document.body.removeChild(wrapper);
  }

  const pdf = new jsPDF('l', 'mm', 'a4'); // landscape
  const pageW = 297, pageH = 210, margin = 8;
  const contentW = pageW - margin * 2;
  const contentH = pageH - margin * 2;
  const pxPerMm = canvas.width / contentW;
  const pageHpx = contentH * pxPerMm;
  let renderedH = 0, pageNum = 0;
  while (renderedH < canvas.height - 1) {
    if (pageNum > 0) pdf.addPage();
    const sliceH = Math.min(pageHpx, canvas.height - renderedH);
    const slice = document.createElement('canvas');
    slice.width = canvas.width; slice.height = sliceH;
    const ctx = slice.getContext('2d');
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, slice.width, slice.height);
    ctx.drawImage(canvas, 0, renderedH, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
    pdf.addImage(slice.toDataURL('image/png'), 'PNG', margin, margin, contentW, sliceH / pxPerMm);
    renderedH += sliceH;
    pageNum++;
  }
  pdf.save(`${title || 'report'}`.replace(/\s+/g, '_') + '.pdf');
}
